import { useCallback, useEffect, useState } from "react"
import { StreamChat } from "stream-chat"

/**
 * Calculate unread count only from channels that have actual messages.
 * This filters out empty channels that may have been created but have no content,
 * as well as channels where all messages predate the user's membership (e.g.
 * pre-existing welcome channels that a newly created user is added to).
 */
const calculateUnreadFromChannels = async (
  client: StreamChat<any>,
): Promise<number> => {
  try {
    const limit = 100
    let offset = 0
    let hasMore = true
    let totalUnread = 0
    const userId = client.userID || ""

    while (hasMore) {
      const channels = await client.queryChannels(
        {
          members: { $in: [userId] },
          type: "messaging",
        },
        { last_message_at: -1 },
        {
          limit,
          offset,
          state: true,
          message_limit: 0,
        },
      )

      totalUnread += channels.reduce((total, channel) => {
        const channelData = channel.data as { last_message_at?: string }
        if (!channelData?.last_message_at || channel.countUnread() <= 0) {
          return total
        }

        // Skip channels where all messages predate the user's membership.
        // For newly created users added to channels with pre-existing messages,
        // countUnread() would incorrectly report those messages as unread
        // because the user has no read state yet.
        const member = channel.state.members[userId]
        if (member?.created_at && channelData.last_message_at) {
          const memberJoinedAt = new Date(member.created_at).getTime()
          const lastMessageAt = new Date(channelData.last_message_at).getTime()
          if (memberJoinedAt >= lastMessageAt) {
            return total
          }
        }

        return total + channel.countUnread()
      }, 0)

      if (channels.length < limit) {
        hasMore = false
      } else {
        offset += limit
      }
    }

    return totalUnread
  } catch {
    return 0
  }
}

/**
 * Simple unread count hook that uses Stream's client-side event tracking.
 * Only counts unread messages from channels that have actual messages,
 * filtering out empty channels that may exist for new users.
 */
export const useSimpleUnreadCount = (client?: StreamChat<any>) => {
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const updateUnreadCount = useCallback(async () => {
    if (!client?.user) return
    const count = await calculateUnreadFromChannels(client)
    setUnreadCount(count)
  }, [client])

  useEffect(() => {
    if (!client?.user) {
      return
    }

    const initializeCount = async () => {
      await updateUnreadCount()
      setIsLoading(false)
    }

    void initializeCount()

    const handleUnreadChange = () => {
      void updateUnreadCount()
    }

    client.on("notification.mark_read", handleUnreadChange)
    client.on("notification.message_new", handleUnreadChange)
    client.on("message.new", handleUnreadChange)
    client.on("message.read", handleUnreadChange)

    return () => {
      client.off("notification.mark_read", handleUnreadChange)
      client.off("notification.message_new", handleUnreadChange)
      client.off("message.new", handleUnreadChange)
      client.off("message.read", handleUnreadChange)
    }
  }, [client, client?.user, updateUnreadCount])

  return {
    unreadCount,
    isLoading,
  }
}

